<template>
  <div class="event-layout">
    <EventSidebar :event-id="eventId" />

    <main class="event-main">
      <div class="page-header">
        <div>
          <p class="breadcrumb">Events → {{ eventName }}</p>
          <h1 class="page-title">Pronunciation Review</h1>
          <p class="page-subtitle">Review student-submitted pronunciation recordings and selected alternatives before they become active for the event.</p>
        </div>
        <div class="header-actions">
          <button class="secondary-button" type="button" @click="downloadReviewExport" :disabled="exporting">
            Export CSV
          </button>
          <button class="secondary-button" type="button" @click="loadStudents">
            Refresh
          </button>
        </div>
      </div>

      <div v-if="error" class="notice error">{{ error }}</div>
      <div v-if="successMessage" class="notice success">{{ successMessage }}</div>

      <section class="panel">
        <div class="filter-row">
          <button
            v-for="option in filterOptions"
            :key="option.value"
            class="filter-chip"
            :class="{ active: statusFilter === option.value }"
            type="button"
            @click="statusFilter = option.value"
          >
            {{ option.label }} <span>{{ statusCount(option.value) }}</span>
          </button>
        </div>

        <div v-if="loading" class="empty-panel">Loading review queue...</div>
        <div v-else-if="filteredStudents.length === 0" class="empty-panel">No students match this filter.</div>
        <div v-else class="review-list">
          <article v-for="student in filteredStudents" :key="student.studentId" class="review-row">
            <div class="student-summary">
              <div class="student-name">{{ student.fullName }}</div>
              <div class="student-meta">{{ student.award || "No award" }}</div>
              <span class="status-pill" :class="`status-${collectionStatus(student)}`">
                {{ statusLabel(collectionStatus(student)) }}
              </span>
              <div
                v-if="isAlternative(student)"
                class="correction-details"
                :class="{ incomplete: !hasCompleteAlternative(student) }"
              >
                <div class="correction-field">
                  <span>Changed part</span>
                  <strong>{{ selectedAlternative(student).partText || "Unknown" }}</strong>
                </div>
                <div class="correction-field">
                  <span>Correction</span>
                  <strong>
                    {{ selectedAlternative(student).originalPhonetic || "Original unavailable" }}
                    to {{ selectedAlternative(student).phonetic || "Unknown" }}
                  </strong>
                </div>
                <div class="correction-field full-pronunciation">
                  <span>Full pronunciation</span>
                  <strong>{{ student.collection.submittedPhonetics || "Needs full-name rebuild" }}</strong>
                </div>
              </div>
            </div>

            <div class="review-actions">
              <button
                class="icon-button"
                type="button"
                title="Copy student review link"
                @click="copyReviewLink(student)"
                :disabled="actioningStudentId === student.studentId"
              >
                <svg aria-hidden="true" viewBox="0 0 24 24" class="button-icon">
                  <path d="M10 13a5 5 0 0 0 7.07 0l2-2a5 5 0 0 0-7.07-7.07l-1.15 1.15" />
                  <path d="M14 11a5 5 0 0 0-7.07 0l-2 2a5 5 0 0 0 7.07 7.07l1.15-1.15" />
                </svg>
              </button>
              <button
                class="secondary-button"
                type="button"
                @click="playAudio(student, 'generated')"
                :disabled="!student.review?.hasGeneratedAudio || playingKey === `${student.studentId}:generated`"
              >
                Generated
              </button>
              <button
                class="secondary-button"
                type="button"
                @click="playAudio(student, 'submitted')"
                :disabled="!student.review?.hasSubmittedAudio || playingKey === `${student.studentId}:submitted`"
              >
                Submitted
              </button>
              <button
                v-if="isReviewable(student)"
                class="primary-button"
                type="button"
                @click="approve(student)"
                :disabled="actioningStudentId === student.studentId || !canApprove(student)"
                :title="approvalTitle(student)"
              >
                Approve
              </button>
              <button
                v-if="isReviewable(student)"
                class="danger-button"
                type="button"
                @click="reject(student)"
                :disabled="actioningStudentId === student.studentId"
              >
                Reject
              </button>
              <button
                v-if="canReset(student)"
                class="secondary-button"
                type="button"
                @click="reset(student)"
                :disabled="actioningStudentId === student.studentId"
              >
                Reset
              </button>
            </div>
          </article>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import {
  approveStudentPronunciation,
  downloadEventExport,
  fetchEvent,
  fetchEventStudents,
  fetchStudentReviewLink,
  fetchStudentPronunciationAudioUrl,
  rejectStudentPronunciation,
  resetStudentPronunciation,
} from "@/lib/api";
import EventSidebar from "./EventSidebar.vue";

export default {
  name: "StudentReviewView",
  components: {
    EventSidebar,
  },
  data() {
    return {
      snapshot: null,
      students: [],
      loading: true,
      statusFilter: "all",
      exporting: false,
      playingKey: "",
      actioningStudentId: "",
      error: "",
      successMessage: "",
      filterOptions: [
        { value: "all", label: "All" },
        { value: "needs_decision", label: "Needs decision" },
        { value: "waiting", label: "Waiting" },
        { value: "needs_review", label: "Needs review" },
        { value: "selected_alternative", label: "Alternative" },
        { value: "confirmed_generated", label: "Confirmed" },
        { value: "approved", label: "Approved" },
        { value: "rejected", label: "Rejected" },
        { value: "not_started", label: "Not started" },
        { value: "invited", label: "Invited" },
      ],
    };
  },
  computed: {
    eventId() {
      return this.$route.params.eventId;
    },
    eventName() {
      return this.snapshot?.event?.name || this.eventId;
    },
    filteredStudents() {
      if (this.statusFilter === "all") {
        return this.students;
      }
      if (this.statusFilter === 'needs_decision') return this.students.filter(student => ['needs_review', 'selected_alternative'].includes(this.collectionStatus(student)));
      if (this.statusFilter === 'waiting') return this.students.filter(student => ['not_started', 'invited'].includes(this.collectionStatus(student)));
      return this.students.filter((student) => this.collectionStatus(student) === this.statusFilter);
    },
    collectionOpen() {
      return Boolean(
        this.snapshot?.event?.studentPronunciationCollection?.enabled &&
        !this.snapshot?.participants?.locked &&
        !this.snapshot?.workflow?.queuePublished
      );
    },
  },
  methods: {
    async loadStudents() {
      this.loading = true;
      this.error = "";
      try {
        const [snapshot, roster] = await Promise.all([
          fetchEvent(this.eventId),
          fetchEventStudents(this.eventId),
        ]);
        this.snapshot = snapshot;
        this.students = roster.students || [];
      } catch (error) {
        this.error = "Unable to load pronunciation review.";
      } finally {
        this.loading = false;
      }
    },
    collectionStatus(student) {
      return student?.collection?.status || "not_started";
    },
    statusLabel(status) {
      return {
        not_started: "Not started",
        invited: "Invited",
        confirmed_generated: "Confirmed",
        selected_alternative: "Alternative",
        needs_review: "Needs review",
        approved: "Approved",
        rejected: "Rejected",
        closed: "Closed",
      }[status] || "Not started";
    },
    statusCount(status) {
      if (status === "all") {
        return this.students.length;
      }
      return this.students.filter((student) => this.collectionStatus(student) === status).length;
    },
    isReviewable(student) {
      return ["needs_review", "selected_alternative"].includes(this.collectionStatus(student));
    },
    canReset(student) {
      return (
        this.collectionOpen &&
        ["confirmed_generated", "approved", "rejected"].includes(this.collectionStatus(student))
      );
    },
    isAlternative(student) {
      return student?.collection?.submissionType === "namecoach_alternative";
    },
    selectedAlternative(student) {
      return student?.collection?.selectedAlternative || {};
    },
    hasCompleteAlternative(student) {
      const collection = student?.collection || {};
      const selected = this.selectedAlternative(student);
      return Boolean(
        collection.submittedPhonetics &&
        collection.submittedIpa &&
        Array.isArray(collection.submittedParts) &&
        collection.submittedParts.length &&
        selected.originalPhonetic &&
        selected.originalIpa
      );
    },
    canApprove(student) {
      return !this.isAlternative(student) || this.hasCompleteAlternative(student);
    },
    approvalTitle(student) {
      return this.canApprove(student)
        ? "Approve pronunciation"
        : "Rebuild this older correction before approval";
    },
    async playAudio(student, source) {
      this.playingKey = `${student.studentId}:${source}`;
      this.error = "";
      try {
        const { url } = await fetchStudentPronunciationAudioUrl(this.eventId, student.studentId, source);
        const audio = new Audio(url);
        audio.onended = () => {
          this.playingKey = "";
        };
        audio.onerror = () => {
          this.playingKey = "";
        };
        await audio.play();
      } catch (error) {
        this.playingKey = "";
        this.error = "Unable to play pronunciation audio.";
      }
    },
    async copyReviewLink(student) {
      this.actioningStudentId = student.studentId;
      this.error = "";
      this.successMessage = "";
      try {
        const url = await fetchStudentReviewLink(this.eventId, student.studentId);
        const absoluteUrl = url.startsWith("http")
          ? url
          : `${window.location.origin}${url}`;
        await navigator.clipboard.writeText(absoluteUrl);
        this.successMessage = "Review link copied.";
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to copy review link.";
      } finally {
        this.actioningStudentId = "";
      }
    },
    async downloadReviewExport() {
      this.exporting = true;
      this.error = "";
      this.successMessage = "";
      try {
        const blob = await downloadEventExport(this.eventId, "pronunciation-links.csv");
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "pronunciation-links.csv";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        this.successMessage = "Review export downloaded.";
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to download review export.";
      } finally {
        this.exporting = false;
      }
    },
    replaceStudent(updatedStudent) {
      this.students = this.students.map((student) =>
        student.studentId === updatedStudent.studentId ? updatedStudent : student
      );
    },
    async approve(student) {
      this.actioningStudentId = student.studentId;
      this.error = "";
      this.successMessage = "";
      try {
        const updated = await approveStudentPronunciation(this.eventId, student.studentId);
        this.replaceStudent(updated);
        this.successMessage = "Recording approved.";
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to approve recording.";
      } finally {
        this.actioningStudentId = "";
      }
    },
    async reject(student) {
      this.actioningStudentId = student.studentId;
      this.error = "";
      this.successMessage = "";
      try {
        const updated = await rejectStudentPronunciation(this.eventId, student.studentId);
        this.replaceStudent(updated);
        this.successMessage = "Recording rejected.";
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to reject recording.";
      } finally {
        this.actioningStudentId = "";
      }
    },
    async reset(student) {
      if (!window.confirm(`Reset ${student.fullName || "this student's"} pronunciation response?`)) {
        return;
      }
      this.actioningStudentId = student.studentId;
      this.error = "";
      this.successMessage = "";
      try {
        const updated = await resetStudentPronunciation(this.eventId, student.studentId);
        this.replaceStudent(updated);
        this.successMessage = "Student pronunciation response reset.";
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to reset pronunciation response.";
      } finally {
        this.actioningStudentId = "";
      }
    },
  },
  watch: { '$route.query.filter': { immediate: true, handler(value) { this.statusFilter = this.filterOptions.some(option => option.value === value) ? value : 'all'; } } },
  mounted() {
    this.loadStudents();
  },
};
</script>

<style scoped>
.event-layout {
  display: flex;
  gap: 1rem;
  width: calc(100vw - 0.25rem);
  max-width: none;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 0 1rem 2rem;
}

.event-main {
  flex: 1;
  min-width: 0;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.breadcrumb,
.page-subtitle,
.student-meta {
  color: var(--orator-graphite);
}

.page-title {
  margin: 0;
  color: var(--orator-navy);
}

.panel {
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  box-shadow: var(--orator-shadow);
  padding: 1rem;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.filter-chip {
  border: 1px solid var(--orator-steel);
  background: white;
  border-radius: 999px;
  padding: 0.45rem 0.75rem;
  color: var(--orator-arsenic);
  font-weight: 800;
}

.filter-chip.active {
  border-color: var(--orator-blue);
  background: var(--orator-blue);
  color: white;
}

.review-list {
  display: grid;
  gap: 0.75rem;
}

.review-row {
  border: 1px solid var(--orator-smoke);
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
}

.student-name {
  color: var(--orator-navy);
  font-weight: 900;
}

.student-summary {
  min-width: min(100%, 29rem);
}

.correction-details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem 1rem;
  margin-top: 0.8rem;
  padding: 0.75rem;
  border: 1px solid #a7d7c5;
  border-radius: 0.4rem;
  background: #f2fbf7;
}

.correction-details.incomplete {
  border-color: #f2c879;
  background: #fffbeb;
}

.correction-field {
  display: grid;
  gap: 0.2rem;
  min-width: 0;
}

.correction-field span {
  color: var(--orator-graphite);
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
}

.correction-field strong {
  color: var(--orator-navy);
  overflow-wrap: anywhere;
}

.full-pronunciation {
  grid-column: 1 / -1;
}

.review-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.primary-button,
.secondary-button,
.danger-button,
.icon-button {
  border-radius: 0.45rem;
  padding: 0.55rem 0.8rem;
  font-weight: 800;
}

.icon-button {
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  border: 1px solid var(--orator-steel);
  background: white;
  color: var(--orator-blue);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.button-icon {
  width: 1rem;
  height: 1rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.primary-button {
  border: 1px solid var(--orator-blue);
  background: var(--orator-blue);
  color: white;
}

.secondary-button {
  border: 1px solid var(--orator-steel);
  background: white;
  color: var(--orator-navy);
}

.danger-button {
  border: 1px solid #b42318;
  background: #b42318;
  color: white;
}

button:disabled {
  opacity: 0.6;
}

.status-pill {
  display: inline-flex;
  margin-top: 0.5rem;
  border-radius: 999px;
  padding: 0.25rem 0.55rem;
  font-weight: 800;
  font-size: 0.78rem;
  background: var(--orator-cloud);
  color: var(--orator-arsenic);
}

.status-needs_review,
.status-selected_alternative {
  background: #fffbeb;
  color: #92400e;
}

.status-approved,
.status-confirmed_generated {
  background: #ecfdf3;
  color: #027a48;
}

.status-rejected {
  background: #fff1f0;
  color: #b42318;
}

.empty-panel {
  color: var(--orator-graphite);
  padding: 2rem;
  text-align: center;
  border: 1px dashed var(--orator-steel);
  border-radius: 0.5rem;
}

.notice {
  margin-bottom: 1rem;
  border-radius: 0.5rem;
  padding: 0.75rem;
  font-weight: 700;
}

.notice.error {
  background: #fff1f0;
  color: #b42318;
}

.notice.success {
  background: #ecfdf3;
  color: #027a48;
}

@media (max-width: 960px) {
  .event-layout,
  .review-row,
  .page-header {
    flex-direction: column;
  }

  .review-actions {
    justify-content: flex-start;
  }

  .student-summary {
    min-width: 0;
    width: 100%;
  }

  .correction-details {
    grid-template-columns: 1fr;
  }

  .full-pronunciation {
    grid-column: auto;
  }
}
</style>
